import { errorCausedByLostContext } from "@classdojo/monsterverse-webgl-context-helpers";

import { checkForFetchError } from "../utils/FetchWrapper";
import { ErrorHandling, type ErrorHandlingReason, HandlingMethod } from "./ErrorHandling";
import { errorToFields } from "./ErrorHandlingUtilities";
import { Logger } from "./Log";
export const sharedErrorHandlingCases = (
  doReturnUserToApp: (reason: string, error?: Error, unexpected?: boolean) => void
) => {
  const sharedErrors: Array<ErrorHandlingReason> = [
    {
      reason: "Fetch error already logged",
      handleConditions: [
        {
          method: HandlingMethod.DisregardError,
          isCondition: (error) => {
            return checkForFetchError(error);
          },
          onHandle: (error?: Error) => doReturnUserToApp("FetchError", error, true),
        },
      ],
    },
    {
      reason: "API Errors already logged",
      handleConditions: [
        {
          method: HandlingMethod.DisregardError,
          isCondition: (error) => {
            const errors = ErrorHandling.flattenErrors(error);
            return errors.find((error) => error.name === "APIError");
          },
          onHandle: (error?: Error) => doReturnUserToApp("APIError", error, true),
        },
      ],
    },
    {
      reason: "MonsterBuilder encountered a known context lost error.",
      handleConditions: [
        {
          method: HandlingMethod.DisregardError,
          isCondition: (error) => {
            const ignore =
              // Condition: MonsterBuilder colorizeTextureWithMask() -- known error occuring due to context losses.
              (error.message?.includes("MonsterBuilder colorizeTextureWithMask()") ||
                // Condition: MonsterBuilder buildAdvanced() -- also another known error occuring due to context losses.
                error.message?.includes("MonsterBuilder buildAdvanced()")) &&
              // Require specific context lost messages...
              errorCausedByLostContext(error);

            if (ignore) {
              Logger.logInfo(error.message, errorToFields(error));
            }

            return ignore;
          },
          onHandle: (error?: Error) => {
            doReturnUserToApp("MonsterBuilderWebGLContextLost", error, true);
          },
        },
      ],
    },
    {
      reason: "Chrome extension",
      handleConditions: [
        {
          method: HandlingMethod.DisregardError,
          isCondition: (error) => {
            if (error.stack?.includes("chrome-extension://")) {
              Logger.logInfo("Disregarded chrome extension error", errorToFields(error));
              return true;
            }
            return false;
          },
        },
      ],
    },
    {
      reason: "Known audio bug on Mac OS 10.15.7", //https://bugs.webkit.org/show_bug.cgi?id=231872
      handleConditions: [
        {
          method: HandlingMethod.DisregardError,
          isCondition: (error) => {
            //Ideally this would check Mac OS and 10.15.7 as the version, however we don't have that info client side
            //This is still logging as info so that we can alert on it and ensure there aren't any false positives here
            if (error.message === "EncodingError: Decoding failed") {
              Logger.logInfo("Disregarded known audio bug on Mac OS 10.15.7", errorToFields(error));
              return true;
            }
            return false;
          },
        },
      ],
    },
    {
      reason: "Error that isn't causing any issues for end users (confirmed by product logs)",
      handleConditions: [
        {
          method: HandlingMethod.DisregardError,
          isCondition: (error) => {
            const ignore = error.message?.startsWith("Unknown error in promise rejection");
            if (ignore) {
              Logger.logInfo("Disregarded unknown error in promise rejection", errorToFields(error));
            }
            return ignore;
          },
        },
      ],
    },
    {
      reason: "Innocuous error thrown by basis.js",
      handleConditions: [
        {
          method: HandlingMethod.DisregardError,
          isCondition: (error) => {
            const errorString = `${error}`;
            const ignore =
              errorString.includes('evaluating \'Module["ready"') ||
              errorString.includes("Cannot set properties of null (setting 'ready')");
            if (ignore) {
              Logger.logInfo("Disregarded basis.js error", errorToFields(error));
            }
            return ignore;
          },
        },
      ],
    },
    {
      reason:
        "Disregard error configuring extra asset configs, failed to fetch, probable restrictive networking policy",
      handleConditions: [
        {
          method: HandlingMethod.DisregardError,
          isCondition: (error) => {
            const errorString = `${error}`;
            const ignore =
              errorString.includes("Error configuring extra asset configs") && errorString.includes("Failed to fetch");
            if (ignore) {
              Logger.logInfo(
                "Disregard error configuring extra asset configs, failed to fetch, probable restrictive networking policy",
                errorToFields(error)
              );
            }
            return ignore;
          },
        },
      ],
    },
  ];
  return sharedErrors;
};
